import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import VehicleTaxService from "../services/vehicleTaxService";
import StorageService from "../services/StorageService";
import Breadcrumb from "../components/Breadcrumb";
import Loading from "../components/loading";
import ReactPaginate from "react-paginate";
import * as XLSX from "xlsx-js-style";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import utility from "../utils/utility";
import MessageDialog from "../components/MessageDialog";


export default function ViewVehicleTax() {
  const [taxes, setTaxes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  // const messageTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Message Helper
  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const { permission, checkView, can } = usePermission(
    ScreenData.Vehicle_Tax,
    showMessage
  );

  useEffect(() => {
    if (permission) {
      if (checkView()) {
        fetchVehicleTaxes();
      }
    }
  }, [currentPage, permission, searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  // const closeMessage = () => {
  //   setMessage("");
  //   if (messageTimer.current) clearTimeout(messageTimer.current);
  // };


  const fetchVehicleTaxes = async () => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany().companyId;
      const search = searchText.trim().toLowerCase();
      // const res = await VehicleTaxService.getByCompany(companyId);
      // const data = res.data || res || [];
      // setTaxes(data.sort((a, b) => b.id - a.id));

      const res = search
        ? await VehicleTaxService.exportVehicleTax(companyId)
        : await VehicleTaxService.getAll(
          companyId,
          currentPage,
          itemsPerPage
        );

const result = res.data || {};
let data = search ? result : result.data || [];

if (search) {
  data = data.filter(
    (t) =>
      t.vehicleName?.toLowerCase().includes(search) ||
      t.registrationNo?.toLowerCase().includes(search) ||
      t.vehicleType?.toLowerCase().includes(search)
  );
}

setTaxes(search ? data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : data);
setPageCount(search ? Math.ceil(data.length / itemsPerPage) : result.totalPages || 0);
    } catch (err) {
      console.error("Error fetching vehicle tax:", err);
      showMessage("Failed to load vehicle tax data", "error");
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchVehicleTaxes();

  //   const msg = localStorage.getItem("vehicleTaxMessage");
  //   if (msg) {
  //     showMessage(msg, "success");
  //     localStorage.removeItem("vehicleTaxMessage");
  //   }
  // }, []);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchVehicleTaxes();
      navigate(location.pathname, { replace: true });
    }
  }, [location]);



  const handleAdd = () => {
    if (!can("canAdd")) {
      showMessage("No permission", "error");
      return;
    }
    navigate("/cpnl/vehicletax/add");
  };

  const exportExcel = async () => {
    if (!can("canView")) {
      showMessage("No permission", "error");
      return;
    }
    try {
      const company = StorageService.activeCompany();

      const res = await VehicleTaxService.exportVehicleTax(
        company.companyId
      );

      if (!res?.status || !res.data.length) {
        showMessage("No data to export", "error");
        return;
      }

      const vehicleTaxes = res.data;

      const wb = XLSX.utils.book_new();
      const ws = {};
      ws["!merges"] = [];

      let row = 0;

      // ✅ STYLES
      const thin = { style: "thin" };
      const thick = { style: "medium" };

      const center = {
        horizontal: "center",
        vertical: "center",
        wrapText: true
      };

      // =========================
      // TITLE
      // =========================
      XLSX.utils.sheet_add_aoa(ws, [["Vehicle Tax Report"]], {
        origin: { r: row, c: 1 }
      });

      ws["!merges"].push({
        s: { r: row, c: 1 },
        e: { r: row, c: 8 }
      });

      const titleCell = XLSX.utils.encode_cell({ r: row, c: 1 });

      ws[titleCell].s = {
        font: { bold: true, sz: 16 },
        alignment: center
      };

      row += 2;

      // =========================
      // HEADER
      // =========================
      const header = [
        "Company",
        "Vehicle Type",
        "Vehicle",
        "Registration No",
        "Tax Paid Date",
        "Next Tax Date",
        "Status"
      ];

      const startRow = row; // ✅ START TRACK

      XLSX.utils.sheet_add_aoa(ws, [header], {
        origin: { r: row, c: 1 }
      });

      header.forEach((_, i) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: i + 1 });

        ws[cell].s = {
          font: { bold: true },
          alignment: center
        };
      });

      row++;

      // =========================
      // DATA
      // =========================
      vehicleTaxes.forEach((v) => {
        const rowData = [
          v.companyName,
          v.vehicleType,
          v.vehicleName,
          v.registrationNo,
          v.taxPaidDate
            ? utility.formatDateDDMMMYYYY(v.taxPaidDate)
            : "-",

          v.nextTaxDate
            ? utility.formatDateDDMMMYYYY(v.nextTaxDate)
            : "-",
          v.status
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], {
          origin: { r: row, c: 1 }
        });

        row++;
      });

      const endRow = row - 1; // ✅ END TRACK

      // =========================
      // APPLY BORDERS 🔥
      // =========================
      const colCount = header.length;

      for (let r = startRow; r <= endRow; r++) {
        for (let c = 1; c <= colCount; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });

          if (!ws[cell]) {
            ws[cell] = { t: "s", v: "" };
          }

          ws[cell].s = ws[cell].s || {};
          ws[cell].s.alignment = center;

          ws[cell].s.border = {
            top: r === startRow ? thick : thin,
            bottom: r === endRow ? thick : thin,
            left: c === 1 ? thick : thin,
            right: c === colCount ? thick : thin
          };
        }
      }

      // =========================
      // COLUMN WIDTH
      // =========================
      ws["!cols"] = [
        { wch: 20 },
        { wch: 18 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 12 }
      ];

      // =========================
      // EXPORT
      // =========================
      XLSX.utils.book_append_sheet(wb, ws, "VehicleTax");

      XLSX.writeFile(wb, "VehicleTax.xlsx");

    } catch (error) {
      console.error(error);
      showMessage("Error exporting data", "error");
    }
  };

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1;
    setCurrentPage(selectedPage);
  };

  // ✅ MODIFY (RADIO)
  const handleModify = () => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }
    if (!selected) {
      showMessage("Please select one Vehicle Tax to modify.", "error");
      return;
    }
    navigate(`/cpnl/vehicletax/add/${selected}`);
  };

  const handleToggleStatus = async (tax) => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }
    const newStatus = tax.status === "Active" ? "Inactive" : "Active";

    if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`))
      return;

    setLoading(true);
    try {
      const res = await VehicleTaxService.updateStatus(tax.id);
      showMessage(res.message || "Status updated successfully", "success");
      fetchVehicleTaxes();
    } catch (error) {
      console.error("Error updating status:", error);
      showMessage("Error updating status", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ RADIO SELECT
  const handleSelect = (id) => {
    setSelected(id);
  };

  // ✅ LIVE SEARCH FILTER
  // const filteredTaxes = taxes.filter(
  //   (t) =>
  //     t.vehicleName?.toLowerCase().includes(searchText.toLowerCase()) ||
  //     t.registrationNo?.toLowerCase().includes(searchText.toLowerCase()) ||
  //     t.vehicleType?.toLowerCase().includes(searchText.toLowerCase())
  // );

  // ✅ LIVE SEARCH FILTER
  const filteredTaxes = taxes;

  return (
    <>
      <Breadcrumb pageTitle="Vehicle Tax" secondTitle="Vehicle Management" />

      {/* {message && (
        <div
          className={`alert ${
            msgType === "success" ? "alert-success" : "alert-danger"
          } d-flex justify-content-between align-items-center mb-2 p-2`}
        >
          <span>{message}</span>
          <button type="button" className="btn-close" onClick={closeMessage}></button>
        </div>
      )} */}


      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />


      <div className="row">
        <div className="card p-3 bg-white shadow-sm">
          {/* <div className="mb-3 pb-2 border-bottom">
            <button
              className="d-inline-flex btn btn-success me-2"
              onClick={handleAdd}
              disabled={loading}
            >
              <i className="ti ti-plus me-1"></i> Add
            </button>

            <button
              className="d-inline-flex btn btn-warning me-2"
              onClick={handleModify}
              disabled={loading}
            >
              <i className="ti ti-pencil me-1"></i> Modify
            </button>
          </div> */}

          <div className="mb-3 pb-2 border-bottom d-flex align-items-end gap-3">

            {/* ✅ SEARCH FIELD */}
            <div>
              <input
                type="text"
                className="form-control"
                placeholder="Search Vehicle Tax..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            {/* BUTTONS */}

            <div className="ms-auto d-flex gap-2">

              {can("canAdd") && (
                <button
                  className="d-inline-flex btn btn-success"
                  onClick={handleAdd}
                  disabled={loading}
                >
                  <i className="ti ti-plus me-1"></i> Add
                </button>
              )}

              {can("canModify") && (
                <button
                  className="d-inline-flex btn btn-warning"
                  onClick={handleModify}
                  disabled={loading}
                >
                  <i className="ti ti-pencil me-1"></i> Modify
                </button>
              )}

              {can("canView") && (
                <button className="btn btn-success" onClick={exportExcel}>
                  <i className="ti ti-download me-1"></i> Export Excel
                </button>
              )}

            </div>


          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>Select</th>
                  <th>Vehicle Type</th>
                  <th>Vehicle</th>
                  <th>Tax Paid Date</th>
                  <th>Next Tax Date</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <Loading />
                    </td>
                  </tr>
                ) : filteredTaxes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">
                      No vehicle tax records found
                    </td>
                  </tr>
                ) : (
                  filteredTaxes.map((tax) => {
                    const isActive =
                      tax.status === "Active" ||
                      tax.status === 1 ||
                      tax.status === true;

                    return (
                      <tr key={tax.id}>
                        <td>
                          <input
                            type="radio"
                            name="vehicleTaxSelect"
                            checked={selected === tax.id}
                            onChange={() => handleSelect(tax.id)}
                          />
                        </td>
                        <td>{tax.vehicleType || tax.vehicleTypeName || "-"}</td>
                        <td>{tax.vehicleName || "-"}-{tax.registrationNo || "-"}</td>
                        <td>{utility.formatDateDDMMMYYYY(tax.taxPaidDate)}</td>
                        <td>{utility.formatDateDDMMMYYYY(tax.nextTaxDate)}</td>
                        <td>
                          <span
                            className={`badge ${isActive ? "bg-success" : "bg-secondary"
                              }`}
                            onClick={() => handleToggleStatus(tax)}
                            style={{ cursor: "pointer" }}
                          >
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={pageCount}
              previousLabel="< Previous"
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
            />
          </div>
        </div>
      </div>
    </>
  );
}
